import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { addPricingPlan, fetchPricingPlan, updatePricingPlan, deletePricingPlan } from './pricingPlanAsyncThunk';
import { IAuthResponse } from '../auth/IAuthState';
import { PricingPlan } from '../../../types';
import { toast } from 'react-toastify';

export interface DeletePricingPlan extends IAuthResponse {
    id: string;
}

interface PricingPlanState {
    data: PricingPlan[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    loading: boolean;
    error: string | null;
}

const initialState: PricingPlanState = {
    data: [],
    status: 'idle',
    loading: false,
    error: null,
};

// Slice
const PricingPlanTypeSlice = createSlice({
    name: 'pricingPrice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchPricingPlan.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchPricingPlan.fulfilled, (state, action: PayloadAction<PricingPlan[]>) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchPricingPlan.rejected, (state, action) => {
                state.status = 'failed';
                toast.error(action.payload || 'Failed to fetch establishmentType');
                state.error = action.payload || 'Failed to fetch establishmentType';
            });

        builder
            .addCase(addPricingPlan.pending, (state) => {
                state.loading = true;
            })
            .addCase(addPricingPlan.fulfilled, (state, action: PayloadAction<PricingPlan>) => {
                state.loading = false;
                state.data.push(action.payload);
            })
            .addCase(addPricingPlan.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add establishmentType');
                state.error = action.payload || 'Failed to add establishmentType';
            });

        builder
            .addCase(updatePricingPlan.pending, (state) => {
                state.loading = true;
            })
            .addCase(updatePricingPlan.fulfilled, (state, action: PayloadAction<PricingPlan>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
                state.loading = false;
            })
            .addCase(updatePricingPlan.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add establishmentType');
                state.error = action.payload || 'Failed to update establishmentType';
            });

        builder
            .addCase(deletePricingPlan.pending, (state) => {
                state.loading = true;
            })
            .addCase(deletePricingPlan.fulfilled, (state, action: PayloadAction<DeletePricingPlan>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data = state.data.filter((item) => item.id !== action.payload.id);
                }
                state.loading = false;
            })
            .addCase(deletePricingPlan.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add pricingPlan');
                state.error = action.payload || 'Failed to delete pricingPlan';
            })
    },
});

export const selectPricingPlan = (state: RootState) => state.pricingPlan;


export default PricingPlanTypeSlice.reducer;
